import {
  cn,
  focusStyles,
  IconButton,
  Input,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
  ToggleGroup,
  ToggleGroupItem,
} from "@beweegbaas/ui";
import { PointerSensor, useDragDropMonitor, useDraggable, useDroppable } from "@dnd-kit/react";
import { useAtomSet } from "@effect/atom-react";
import { useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { formatLesuur, type Lesuur } from "#/modules/les.planning/domain/lesrooster";
import {
  getShortName,
  type RoosterDocent,
  type Roosterregel,
} from "#/modules/les.planning/domain/rooster";
import {
  changeRegelAtom,
  createRegelAtom,
  deleteRegelAtom,
} from "#/modules/les.planning/rooster/rooster-editor";
import type { components } from "#/schema";

export type Klas = components["schemas"]["KlasOverviewModel"];

/** Where a les of the rooster is dropped: a lesuur of the day, or the tab of another weekdag. */
export type DropData =
  | { readonly kind: "lesuur"; readonly weekdag: number; readonly lesuurId: string }
  | { readonly kind: "weekdag"; readonly weekdag: number };

// Made in the browser, not saved yet: it shows, but cannot be changed until the server has it.
const isUnsaved = (regel: Roosterregel) => regel.id.startsWith("new:");

// Only a pointer moves a les: Enter and Space on its card open it.
const CARD_SENSORS = [PointerSensor];
const CLICK_SLOP = 5;

const KLAS_KEY = /[\p{L}\p{N}]/u;

const isKlasKey = (event: KeyboardEvent) =>
  event.key.length === 1 && KLAS_KEY.test(event.key) && !event.ctrlKey && !event.metaKey;

const klasNames = (regel: Roosterregel) => regel.klassen.map((klas) => klas.name).join(", ");

const NO_KLASSEN: readonly string[] = [];

const ROW =
  "hover:bg-accent focus-visible:bg-accent flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left outline-none transition-colors";

/**
 * A lesuur of the day with its lessen side by side, each as wide as the others. An empty lesuur is
 * a dashed box that adds a les; a les is a card that opens everything there is to change about it.
 */
export function RoosterCel({
  lesuur,
  weekdagName,
  regels,
  klassen,
  docenten,
}: {
  lesuur: Lesuur;
  weekdagName: string;
  regels: readonly Roosterregel[];
  klassen: readonly Klas[];
  docenten: readonly RoosterDocent[];
}) {
  const { ref, isDropTarget } = useDroppable({
    id: `lesuur|${lesuur.id}`,
    data: { kind: "lesuur", weekdag: lesuur.weekdag, lesuurId: lesuur.id } satisfies DropData,
  });
  const createRegel = useAtomSet(createRegelAtom);
  const lesuurName = formatLesuur(lesuur);
  const title = m["planning.rooster.cel"]({ dag: weekdagName, lesuur: lesuurName });
  // A new les is given by the docent who adds it, until someone else is picked for it.
  const addLes = (klas: Klas) =>
    createRegel({
      klassen: [{ id: klas.id, name: klas.name }],
      lesuur,
      docent: docenten.find((docent) => docent.isCurrentUser),
    });

  return (
    <div
      ref={ref}
      role="cell"
      className={cn(
        "flex h-full min-w-0 flex-1 gap-1 squircle rounded-lg supports-squircle:rounded-2xl p-0.75 transition-colors",
        isDropTarget && "bg-primary/10",
      )}
      data-rooster-cel={`${lesuur.weekdag}|${lesuur.nummer}`}
    >
      {regels.length === 0 ? (
        <EmptyLesuur title={title} klassen={klassen} onPick={addLes} />
      ) : (
        regels.map((regel) => (
          <LesCard
            key={regel.id}
            regel={regel}
            title={title}
            lesuurName={lesuurName}
            weekdagName={weekdagName}
            klassen={klassen}
            docenten={docenten}
            onAddLes={addLes}
          />
        ))
      )}
    </div>
  );
}

/** A popover with a klas search, which typing on its trigger opens with what was typed. */
function useKlasSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const queryRef = useRef<HTMLInputElement>(null);
  return {
    open,
    setOpen,
    query,
    setQuery,
    queryRef,
    typeToOpen: (event: KeyboardEvent) => {
      if (!isKlasKey(event)) return;
      event.preventDefault();
      setQuery(event.key);
      setOpen(true);
    },
  };
}

/** An empty lesuur: the klas tapped in its picker gets a les there. */
function EmptyLesuur({
  title,
  klassen,
  onPick,
}: {
  title: string;
  klassen: readonly Klas[];
  onPick: (klas: Klas) => void;
}) {
  const search = useKlasSearch();
  return (
    <Popover
      open={search.open}
      onOpenChange={search.setOpen}
      onOpenChangeComplete={(open) => {
        if (!open) search.setQuery("");
      }}
    >
      <PopoverTrigger
        onKeyDown={search.typeToOpen}
        render={
          <button
            type="button"
            aria-label={title}
            className={cn(
              "group/leeg border-foreground/12 text-muted-foreground flex min-h-0 min-w-0 flex-1 cursor-pointer items-center justify-center squircle rounded-lg supports-squircle:rounded-2xl border border-dashed",
              focusStyles,
              "transition-colors hover:border-primary/40 hover:bg-primary/[0.04] hover:text-primary",
              "data-[popup-open]:border-ring data-[popup-open]:border-solid",
            )}
          />
        }
      >
        <IconMaterialSymbolsAdd className="size-4 opacity-0 transition-opacity group-hover/leeg:opacity-100" />
      </PopoverTrigger>
      {/* The popup takes focus itself unless told where: typing goes on in the search field. */}
      <PopoverContent
        align="start"
        className="w-72 gap-2.5"
        initialFocus={search.queryRef}
        data-testid="roosteruur-popover"
      >
        <PopoverTitle className="m-0 text-xs font-semibold first-letter:uppercase">
          {title}
        </PopoverTitle>
        <KlasPicker
          klassen={klassen}
          query={search.query}
          queryRef={search.queryRef}
          onQueryChange={search.setQuery}
          onPick={(klas) => {
            onPick(klas);
            search.setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * One les of the rooster as a card: its klassen and who gives it. The card opens all of it: the
 * klassen, the docent, taking the les away, and another les in the same lesuur.
 */
function LesCard({
  regel,
  title,
  lesuurName,
  weekdagName,
  klassen,
  docenten,
  onAddLes,
}: {
  regel: Roosterregel;
  /** The lesuur on its day. */
  title: string;
  lesuurName: string;
  weekdagName: string;
  klassen: readonly Klas[];
  docenten: readonly RoosterDocent[];
  onAddLes: (klas: Klas) => void;
}) {
  const unsaved = isUnsaved(regel);
  const drag = useDraggable({
    id: regel.id,
    data: regel,
    disabled: unsaved,
    sensors: CARD_SENSORS,
  });
  const changeRegel = useAtomSet(changeRegelAtom);
  const deleteRegel = useAtomSet(deleteRegelAtom);
  const search = useKlasSearch();
  // The picker is for the klas of another les in this lesuur, instead of the klassen of this one.
  const [addingLes, setAddingLes] = useState(false);
  const names = klasNames(regel);
  const selected = regel.klassen.map((klas) => klas.id);
  const docent = docenten.find((item) => item.schooldocentId === regel.schooldocentId);
  const docentName = docent?.name ?? m["planning.rooster.onbekende_docent"]();

  // A press held still picks the card up too; let go where it lay, it was a click after all.
  useDragDropMonitor({
    onDragEnd(event) {
      const { source, transform } = event.operation;
      if (event.canceled || source?.id !== regel.id) return;
      if (Math.hypot(transform.x, transform.y) < CLICK_SLOP) search.setOpen(true);
    },
  });

  const remove = () => {
    search.setOpen(false);
    deleteRegel(regel);
  };

  const toggleKlas = (klas: Klas) => {
    search.setQuery("");
    const klasIds = selected.includes(klas.id)
      ? selected.filter((id) => id !== klas.id)
      : [...selected, klas.id];
    if (klasIds.length === 0) {
      remove();
      return;
    }
    // A klas the list does not hold (any more) stays in the les as it was.
    const known = new Map(
      [...regel.klassen, ...klassen].map((item) => [item.id, { id: item.id, name: item.name }]),
    );
    changeRegel({
      regel,
      change: { klassen: klasIds.flatMap((id) => known.get(id) ?? []) },
    });
  };

  const pickDocent = (item: RoosterDocent) => {
    if (item.schooldocentId === regel.schooldocentId) return;
    changeRegel({
      regel,
      change: { schooldocentId: item.schooldocentId, isCurrentUser: item.isCurrentUser },
    });
  };

  // Focus goes to the search field before the button that was pressed goes away, so it stays in
  // the popover.
  const switchView = (adding: boolean) => {
    search.queryRef.current?.focus();
    search.setQuery("");
    setAddingLes(adding);
  };

  return (
    <Popover
      open={search.open}
      onOpenChange={search.setOpen}
      onOpenChangeComplete={(open) => {
        if (open) return;
        search.setQuery("");
        setAddingLes(false);
      }}
    >
      <PopoverTrigger
        ref={drag.ref}
        disabled={unsaved}
        onKeyDown={search.typeToOpen}
        render={
          <button
            type="button"
            aria-label={m["planning.rooster.les"]({
              klas: names,
              lesuur: lesuurName,
              dag: weekdagName,
              docent: docentName,
            })}
            className={cn(
              "@container/les bg-foreground/6 flex h-full min-w-0 flex-1 basis-0 cursor-pointer items-center gap-2 squircle rounded-lg supports-squircle:rounded-2xl px-3 text-left",
              focusStyles,
              "transition-colors enabled:hover:bg-foreground/10 disabled:cursor-default data-[popup-open]:bg-foreground/10",
              drag.isDragging && "opacity-60",
              unsaved && "opacity-70",
            )}
            // A drawer around the rooster leaves the card to the finger that moves it.
            data-base-ui-swipe-ignore
            data-roosterslot={regel.id}
            data-klas={names}
            data-docent={docent?.name}
          />
        }
      >
        <span className="font-heading min-w-0 truncate text-base font-extrabold">{names}</span>
        {/* On a phone the docent leaves the room to the times; the cell's popover names them. */}
        <span className="text-muted-foreground ml-auto max-w-24 min-w-0 truncate text-[13px] font-medium max-md:hidden">
          {docent ? getShortName(docent, docenten) : docentName}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 gap-2.5"
        initialFocus={search.queryRef}
        data-testid="roosteruur-popover"
      >
        {addingLes ? (
          <>
            <div className="-ml-1.5 flex items-center gap-1">
              <IconButton
                size="icon-xs"
                label={m["common.actions.back"]()}
                className="shrink-0"
                onClick={() => switchView(false)}
              >
                <IconMaterialSymbolsChevronLeftRounded className="size-4" />
              </IconButton>
              <PopoverTitle className="m-0 text-xs font-semibold">
                {m["planning.rooster.nog_een_les"]()}
              </PopoverTitle>
            </div>
            <KlasPicker
              klassen={klassen}
              query={search.query}
              queryRef={search.queryRef}
              onQueryChange={search.setQuery}
              onPick={(klas) => {
                onAddLes(klas);
                search.setOpen(false);
              }}
            />
          </>
        ) : (
          <>
            <PopoverTitle className="m-0 text-xs font-semibold first-letter:uppercase">
              {title}
            </PopoverTitle>
            <KlasPicker
              klassen={klassen}
              selected={selected}
              query={search.query}
              queryRef={search.queryRef}
              onQueryChange={search.setQuery}
              onPick={toggleKlas}
            />
            {docenten.length > 0 ? (
              <DocentPicker
                docenten={docenten}
                schooldocentId={regel.schooldocentId}
                onPick={pickDocent}
              />
            ) : null}
            <div className="border-t pt-2">
              <div className="-mx-2 flex flex-col gap-0.5">
                <button type="button" className={ROW} onClick={() => switchView(true)}>
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    <IconMaterialSymbolsAdd className="size-4" />
                  </span>
                  {m["planning.rooster.nog_een_les"]()}
                </button>
                <button
                  type="button"
                  className={cn(
                    ROW,
                    "text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10",
                  )}
                  onClick={remove}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    <IconMaterialSymbolsDeleteOutline className="size-4" />
                  </span>
                  {m["planning.rooster.les_verwijderen"]()}
                </button>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** The klassen to tap, narrowed down by what is typed; Enter takes the first one left. */
function KlasPicker({
  klassen,
  selected,
  query,
  queryRef,
  onQueryChange,
  onPick,
}: {
  klassen: readonly Klas[];
  /** The klassen that are in already, shown pressed. */
  selected?: readonly string[];
  query: string;
  queryRef: RefObject<HTMLInputElement | null>;
  onQueryChange: (query: string) => void;
  onPick: (klas: Klas) => void;
}) {
  const term = query.trim().toLocaleLowerCase("nl");
  const matches = klassen.filter((klas) => klas.name.toLocaleLowerCase("nl").includes(term));
  const pressed = selected ?? NO_KLASSEN;
  return (
    <>
      <Input
        ref={queryRef}
        value={query}
        placeholder={m["planning.rooster.typ_klas"]()}
        aria-label={m["planning.rooster.typ_klas"]()}
        className="h-8"
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          const first = matches[0];
          if (first) onPick(first);
        }}
      />
      <ToggleGroup
        multiple
        value={pressed}
        onValueChange={(value) => {
          const klas = matches.find(
            (item) => value.includes(item.id) !== pressed.includes(item.id),
          );
          if (klas) onPick(klas);
        }}
        size="sm"
        className="grid grid-cols-4 gap-1.5"
        aria-label={m["planning.rooster.klas"]()}
      >
        {matches.map((klas) => (
          <ToggleGroupItem key={klas.id} value={klas.id} className="font-heading font-extrabold">
            {klas.name}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {matches.length === 0 ? (
        <p className="text-muted-foreground m-0 text-xs">{m["planning.rooster.geen_klas"]()}</p>
      ) : null}
    </>
  );
}

/** Who gives the les: a row per docent of the school, the one who gives it checked. */
function DocentPicker({
  docenten,
  schooldocentId,
  onPick,
}: {
  docenten: readonly RoosterDocent[];
  schooldocentId: string;
  onPick: (docent: RoosterDocent) => void;
}) {
  const label = m["planning.rooster.wie_geeft"]();
  return (
    <div className="flex flex-col gap-1.5 border-t pt-2.5">
      <p className="text-muted-foreground m-0 text-xs font-medium">{label}</p>
      <div
        className="-mx-2 flex max-h-52 flex-col gap-0.5 overflow-y-auto"
        role="group"
        aria-label={label}
      >
        {docenten.map((docent) => {
          const gives = docent.schooldocentId === schooldocentId;
          return (
            <button
              key={docent.schooldocentId}
              type="button"
              aria-pressed={gives}
              className={cn(ROW, gives && "bg-accent font-medium")}
              onClick={() => onPick(docent)}
            >
              <span className="min-w-0 flex-1 truncate">
                {docent.name}
                {docent.isCurrentUser ? (
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    {m["planning.rooster.jij"]()}
                  </span>
                ) : null}
              </span>
              {gives ? (
                <IconMaterialSymbolsCheckRounded className="text-primary size-4 shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
